import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, ArrowRight, Loader2 } from "lucide-react";
import { API, PRICING } from "@/data/landing";
import { Reveal } from "@/components/landing/primitives";

export const PricingSection = () => {
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const handleCheckout = async (lookup, testid) => {
    setCheckoutLoading(testid);
    let context = null;
    try { context = JSON.parse(sessionStorage.getItem("afwn_quiz") || "null"); } catch { context = null; }
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        lookup_key: lookup,
        origin_url: window.location.origin,
        context,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error("Could not start checkout. Please try again.");
        setCheckoutLoading(null);
      }
    } catch (err) {
      toast.error("Could not start checkout. Please try again.");
      setCheckoutLoading(null);
    }
  };
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-sm tracking-wider uppercase font-semibold text-afwn-green">Pricing</p>
            <h2 className="mt-3 font-heading font-light tracking-tight text-4xl lg:text-5xl">Start free. Grow on your terms.</h2>
            <p className="mt-4 text-lg text-afwn-slate">Choose 1, 2, or all 3 programs — pick your programs and assistants inside the app. Every plan begins with a <span className="font-medium text-afwn-navy">7-day free trial</span> — no risk, cancel anytime.</p>
          </div>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          {PRICING.map((p, i) => (
            <Reveal key={p.name} i={i}>
              <div data-testid={`${p.testid}-card`} className={`relative rounded-3xl p-8 h-full flex flex-col transition-all duration-300 ${p.popular ? "bg-afwn-green text-white shadow-2xl shadow-afwn-green/25 lg:-translate-y-4 border-2 border-afwn-green" : "bg-white border border-afwn-navy/10 hover:shadow-xl hover:-translate-y-1"}`}>
                {p.popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 text-xs tracking-wider uppercase rounded-full font-semibold bg-afwn-amber text-white">Most Popular</span>}
                <h3 className={`font-heading text-3xl ${p.popular ? "text-white" : "text-afwn-navy"}`}>{p.name}</h3>
                <p data-testid={`${p.testid}-tagline`} className={`mt-1 text-sm ${p.popular ? "text-white/75" : "text-afwn-slate"}`}>{p.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className={`text-2xl font-light ${p.popular ? "text-white/80" : "text-afwn-slate"}`}>$</span>
                  <span className="text-5xl font-heading font-light">{p.price}</span>
                  <span className={`mb-2 ${p.popular ? "text-white/70" : "text-afwn-slate"}`}>/mo</span>
                </div>
                <p className={`mt-1 text-sm ${p.popular ? "text-afwn-amber" : "text-afwn-green"} font-medium`}>7-Day Free Trial included</p>
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check size={18} className={`shrink-0 mt-0.5 ${p.popular ? "text-afwn-amber" : "text-afwn-green"}`} />
                      <span className={p.popular ? "text-white/90" : "text-afwn-slate"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handleCheckout(p.lookup, p.testid)}
                  disabled={checkoutLoading === p.testid}
                  data-testid={`${p.testid}-button`}
                  className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full font-medium px-6 py-3.5 transition-all duration-300 hover:scale-[1.03] disabled:opacity-70 disabled:hover:scale-100 focus:outline-none focus:ring-2 focus:ring-offset-2 ${p.popular ? "bg-afwn-amber text-white hover:brightness-95 focus:ring-afwn-amber" : "bg-afwn-green text-white hover:bg-afwn-greenDark focus:ring-afwn-green"}`}>
                  {checkoutLoading === p.testid ? <><Loader2 size={16} className="animate-spin" /> Redirecting...</> : <>Start Free Trial <ArrowRight size={16} /></>}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
